import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  FormControlLabel
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  Person as PersonIcon 
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';

function UserManagement() {
  const { user } = useAuth();
  const [users, setUsers] = useState([
    {
      id: 1,
      email: 'admin@company.com',
      role: 'admin',
      name: 'System Administrator',
      isActive: true,
      lastLogin: '2024-01-15 14:30:00',
      created: '2024-01-01'
    },
    {
      id: 2,
      email: 'manager@company.com',
      role: 'manager',
      name: 'Business Manager',
      isActive: true,
      lastLogin: '2024-01-20 09:15:00',
      created: '2024-01-05'
    },
    {
      id: 3,
      email: 'viewer@company.com',
      role: 'viewer',
      name: 'Report Viewer',
      isActive: true,
      lastLogin: '2024-01-18 16:45:00',
      created: '2024-01-10'
    }
  ]);
  
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [newUser, setNewUser] = useState({
    email: '',
    name: '',
    role: 'viewer'
  });
  const navigate = useNavigate();

  // Check permissions
  const canCreate = user?.permissions?.users?.includes('create');
  const canUpdate = user?.permissions?.users?.includes('update');
  const canDelete = user?.permissions?.users?.includes('delete');

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setNewUser({
        email: user.email,
        name: user.name,
        role: user.role
      });
    } else {
      setEditingUser(null);
      setNewUser({
        email: '',
        name: '',
        role: 'viewer'
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUser(null);
  };

  const handleSaveUser = () => {
    if (editingUser) {
      // Update existing user
      setUsers(users.map(u => 
        u.id === editingUser.id 
          ? { ...u, ...newUser }
          : u
      ));
    } else {
      // Add new user
      const userToAdd = {
        id: Math.max(...users.map(u => u.id)) + 1,
        ...newUser,
        isActive: true,
        lastLogin: 'Never',
        created: new Date().toISOString().split('T')[0]
      };
      setUsers([...users, userToAdd]);
    }
    handleCloseDialog();
  };

  const handleDeleteUser = (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      setUsers(users.filter(u => u.id !== userId));
    }
  };

  const handleToggleActive = (userId) => {
    setUsers(users.map(u => 
      u.id === userId 
        ? { ...u, isActive: !u.isActive }
        : u
    ));
  };

  const getRoleColor = (role) => {
    const colors = {
      admin: 'error',
      manager: 'warning',
      viewer: 'success'
    };
    return colors[role] || 'default';
  };

  const activeUsers = users.filter(u => u.isActive).length;
  const totalUsers = users.length;

  return (
    <Box>
		{/* Header */}
		<Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
		  <Box display="flex" alignItems="center" gap={2}>
			<IconButton 
			  onClick={() => navigate('/dashboard')}
			  color="primary"
			>
			  <ArrowBackIcon />
			</IconButton>
			<Typography variant="h4">User Management</Typography>
		  </Box>
		  {canCreate && (
			<Button
			  variant="contained"
			  startIcon={<AddIcon />}
			  onClick={() => handleOpenDialog()}
			>
			  Add User
			</Button>
		  )}
		</Box>

      {/* User Statistics */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Users
              </Typography>
              <Typography variant="h4">
                {totalUsers}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Active Users
              </Typography>
              <Typography variant="h4" style={{ color: '#2e7d32' }}>
                {activeUsers}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Admin Users
              </Typography>
              <Typography variant="h4" style={{ color: '#d32f2f' }}>
                {users.filter(u => u.role === 'admin').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Manager Users
              </Typography>
              <Typography variant="h4" style={{ color: '#ed6c02' }}>
                {users.filter(u => u.role === 'manager').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Users Table */}
      <Paper>
        <Box p={2}>
          <Typography variant="h6" gutterBottom>
            System Users
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last Login</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((userItem) => (
                  <TableRow key={userItem.id}>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <PersonIcon style={{ marginRight: '10px', color: '#666' }} />
                        <Box>
                          <Typography variant="body1">
                            {userItem.name}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {userItem.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={userItem.role} 
                        color={getRoleColor(userItem.role)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={userItem.isActive}
                            onChange={() => handleToggleActive(userItem.id)}
                            color="primary"
                            disabled={!canUpdate}
                          />
                        }
                        label={userItem.isActive ? 'Active' : 'Inactive'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {userItem.lastLogin}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {userItem.created}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton 
                        size="small" 
                        onClick={() => handleOpenDialog(userItem)}
                        disabled={!canUpdate}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={() => handleDeleteUser(userItem.id)}
                        disabled={!canDelete}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Paper>

      {/* Add/Edit User Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingUser ? 'Edit User' : 'Add New User'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} style={{ marginTop: '10px' }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Full Name"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Role</InputLabel>
                <Select
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  label="Role"
                >
                  <MenuItem value="viewer">Viewer</MenuItem>
                  <MenuItem value="manager">Manager</MenuItem>
                  <MenuItem value="admin">Administrator</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSaveUser} 
            variant="contained"
            disabled={!newUser.name || !newUser.email}
          >
            {editingUser ? 'Update User' : 'Add User'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default UserManagement;